import { useState, useEffect, useCallback, useRef } from 'react';
import { vendorService, getErrorMessage } from '../services/apiService';

/**
 * Shared ERP vendor-code sync state for every screen that exposes the sync button.
 *
 * The sync is asynchronous server-side: POST /vendors/sync-erp returns 202 immediately
 * and the run reports into a Redis-backed progress blob, so this hook polls
 * GET /vendors/sync-erp/status rather than awaiting the POST. That also means progress
 * survives a page refresh — on mount we simply read whatever the server is doing.
 *
 * @param {object}   opts
 * @param {Function} opts.onComplete Called once with the run summary when a run finishes.
 */
export function useErpSync({ onComplete } = {}) {
  const [progress, setProgress] = useState(null); // { running, scanned, total, startedAt, lastResult }
  const [starting, setStarting] = useState(false); // POST in flight only
  const [elapsedMs, setElapsedMs] = useState(0);

  // Keep the latest callback without making it a polling-effect dependency.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const refresh = useCallback(async () => {
    try {
      const res = await vendorService.getSyncERPStatus();
      if (res.success) {
        setProgress(res.data.progress || null);
        return res.data.progress || null;
      }
    } catch (err) {
      console.error('Error fetching ERP sync status:', err);
    }
    return null;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const running = Boolean(progress?.running);

  // Poll while a run is in flight; announce completion exactly once.
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(async () => {
      const p = await refresh();
      if (p && !p.running) {
        clearInterval(timer);
        onCompleteRef.current?.(p.lastResult || null);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [running, refresh]);

  // Live elapsed clock, anchored to the server's startedAt so a refresh mid-run
  // shows the true elapsed time rather than restarting from zero.
  useEffect(() => {
    if (!running || !progress?.startedAt) {
      setElapsedMs(0);
      return undefined;
    }
    const startedAt = new Date(progress.startedAt).getTime();
    const tick = () => setElapsedMs(Math.max(0, Date.now() - startedAt));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [running, progress?.startedAt]);

  /** Kick off a run. Returns null on success, or an error message string. */
  const start = useCallback(async () => {
    setStarting(true);
    try {
      const res = await vendorService.syncERP();
      if (res.success) {
        await refresh(); // flips progress.running -> the polling effect takes over
        return null;
      }
      return 'Failed to start ERP sync';
    } catch (err) {
      return getErrorMessage(err) || 'Failed to sync with ERP';
    } finally {
      setStarting(false);
    }
  }, [refresh]);

  return {
    progress,
    lastResult: progress?.lastResult || null,
    running,
    starting,
    elapsedMs,
    canSync: !running,
    start,
    refresh,
  };
}

/** "4m 12s" / "45s" / "1h 3m" — compact, no leading zeros. */
export function formatDuration(ms) {
  if (!ms || ms < 0) return '0s';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Rough time remaining from the observed scan rate. Null until enough of the run has
 * elapsed to be meaningful — a wildly wrong ETA is worse than none.
 */
export function estimateRemainingMs(elapsedMs, scanned, total) {
  if (!elapsedMs || !scanned || !total || scanned < 5 || scanned >= total) return null;
  const msPerVendor = elapsedMs / scanned;
  return Math.round((total - scanned) * msPerVendor);
}
